import React from "react";
import { getProgressPoint } from "../../../helpers/bar-helper";
import { BarDisplay } from "./bar-display";
import { BarDateHandle } from "./bar-date-handle";
import { BarProgressHandle } from "./bar-progress-handle";
import { TaskItemProps } from "../task-item";
import { getContrastColor } from "../../../helpers/color-contrast";
import styles from "./bar.module.css";

export const Bar: React.FC<TaskItemProps> = ({
  task,
  isProgressChangeable,
  isDateChangeable,
  rtl,
  onEventStart,
  isSelected,
  taskHeight,
  isCollapsedChainParent,
  chainTasks,
}) => {
  // Render collapsed chain bar
  if (isCollapsedChainParent && chainTasks && chainTasks.length > 1) {
    const parentWidth = task.x2 - task.x1;
    const parentStart = task.start.getTime();
    const parentEnd = task.end.getTime();
    const parentDuration = parentEnd - parentStart;

    // Calculate sub-bar positions and widths
    const subBars = chainTasks.map((chainTask, index) => {
      const taskStart = chainTask.start.getTime();
      const taskEnd = chainTask.end.getTime();
      const taskDuration = taskEnd - taskStart;

      const offsetFromParentStart = Math.max(0, taskStart - parentStart);
      const proportionalX = (offsetFromParentStart / parentDuration) * parentWidth;
      const proportionalWidth = (taskDuration / parentDuration) * parentWidth;

      const validWidth = isNaN(proportionalWidth) || proportionalWidth < 0 ? 0 : proportionalWidth;

      return {
        task: chainTask,
        index,
        x: task.x1 + proportionalX,
        width: Math.max(2, validWidth),
      };
    });

    const getBarColor = () => {
      return isSelected
        ? (task.styles?.backgroundSelectedColor || "#8282f5")
        : (task.styles?.backgroundColor || "#b8c2cc");
    };

    const baseColor = getBarColor();

    const darkenColor = (color: string) => {
      const num = parseInt(color.replace("#", ""), 16);
      const amt = 30;
      const usePound = true;
      const R = (num >> 16) & 255;
      const G = (num >> 8) & 255;
      const B = num & 255;
      return (usePound ? "#" : "") +
        (0x1000000 + (Math.round((R - amt) / 255) > 0 ? R - amt : 0) * 0x10000 +
        (Math.round((G - amt) / 255) > 0 ? G - amt : 0) * 0x100 +
        (Math.round((B - amt) / 255) > 0 ? B - amt : 0))
        .toString(16)
        .slice(1);
    };

    return (
      <g className={styles.barWrapper}>
        <defs>
          <pattern
            id={`diagonalStripes-${task.id}`}
            patternUnits="userSpaceOnUse"
            width="8"
            height="8"
            patternTransform="rotate(-45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="8"
              stroke="rgba(0,0,0,0.15)"
              strokeWidth="4"
            />
          </pattern>
        </defs>

        {/* Background striped pattern */}
        <rect
          x={task.x1}
          width={parentWidth}
          y={task.y}
          height={taskHeight}
          ry={task.barCornerRadius}
          rx={task.barCornerRadius}
          fill={`url(#diagonalStripes-${task.id})`}
          className={styles.barBackground}
        />

        {/* Base color overlay */}
        <rect
          x={task.x1}
          width={parentWidth}
          y={task.y}
          height={taskHeight}
          ry={task.barCornerRadius}
          rx={task.barCornerRadius}
          fill={baseColor}
          fillOpacity={0.15}
        />

        {/* Sub-task segments */}
        {subBars.map((subBar) => {
          const validWidth = isNaN(subBar.width) || subBar.width < 0 ? 0 : subBar.width;
          const taskBgColor = subBar.task.bgcolor || baseColor;
          const progressBgColor = darkenColor(taskBgColor);
          const textColor = getContrastColor(taskBgColor);

          return (
            <g key={`sub-${subBar.task.id}`}>
              {/* Segment rect */}
              <rect
                x={subBar.x}
                width={validWidth}
                y={task.y}
                height={taskHeight}
                ry={task.barCornerRadius}
                rx={task.barCornerRadius}
                fill={taskBgColor}
              />

              {/* Progress indicator */}
              {subBar.task.progress > 0 && (
                <rect
                  x={subBar.x}
                  width={(validWidth * subBar.task.progress) / 100}
                  y={task.y}
                  height={taskHeight}
                  ry={task.barCornerRadius}
                  rx={task.barCornerRadius}
                  fill={progressBgColor}
                />
              )}

              {/* Task name */}
              {validWidth > 20 && (
                <text
                  x={subBar.x + validWidth / 2}
                  y={task.y + taskHeight / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={textColor}
                  fontWeight="bold"
                >
                  {subBar.task.name?.substring(0, 5)}
                </text>
              )}
            </g>
          );
        })}
      </g>
    );
  }

  // Render normal bar
  const progressPoint = getProgressPoint(
    +!rtl * task.progressWidth + task.progressX,
    task.y,
    task.height
  );
  const handleHeight = task.height - 2;

  const taskWidth = task.x2 - task.x1;
  const validTaskWidth = isNaN(taskWidth) ? 0 : taskWidth;
  const validHandleWidth = isNaN(task.handleWidth) ? 8 : task.handleWidth;

  return (
    <g className={styles.barWrapper} tabIndex={0}>
      <BarDisplay
        task={task}
        x={task.x1}
        y={task.y}
        width={validTaskWidth}
        height={task.height}
        progressX={task.progressX}
        progressWidth={task.progressWidth}
        barCornerRadius={task.barCornerRadius}
        styles={task.styles}
        isSelected={isSelected}
        onMouseDown={e => {
          isDateChangeable && onEventStart("move", task, e);
        }}
      />
      <g className="handleGroup">
        {isDateChangeable && validTaskWidth > 0 && (
          <g>
            {/* left */}
            <BarDateHandle
              x={task.x1 + 1}
              y={task.y + 1}
              width={validHandleWidth}
              height={handleHeight}
              barCornerRadius={task.barCornerRadius}
              onMouseDown={e => {
                onEventStart("start", task, e);
              }}
            />
            {/* right */}
            <BarDateHandle
              x={task.x2 - validHandleWidth - 1}
              y={task.y + 1}
              width={validHandleWidth}
              height={handleHeight}
              barCornerRadius={task.barCornerRadius}
              onMouseDown={e => {
                onEventStart("end", task, e);
              }}
            />
          </g>
        )}
        {isProgressChangeable && (
          <BarProgressHandle
            progressPoint={progressPoint}
            onMouseDown={e => {
              onEventStart("progress", task, e);
            }}
          />
        )}
      </g>
    </g>
  );
};
